import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Navigation, Router } from '@angular/router';
import {
  observableOf,
  observableThrowError,
  rx,
  Subscription,
} from '@app/shared/rx';
import {
  COMPANY_ID,
  EMPLOYEE_ID,
  EVENT_ID,
  SITE_TOKEN,
  LOGO_URL,
  SITE_ID,
} from '@config/global-constants';
import { forEach, get, head, isEmpty } from 'lodash';
import { I18n } from '@app/i18n/strings-keys';
import { IToken } from '@shared/types';
import { catchError } from 'rxjs/operators';
import {
  DmDataService,
  TokenSessionService,
  XprtDataService,
} from '@shared/services';
import { SubscriptionCheckService } from '@app/shared/services/subscription-check';
import { VerifyErrorType } from './verify.enum';
import { lazyInject } from '@app/shared/decorators';
import { Location } from '@angular/common';
import { combineLatest } from 'rxjs';
import { ThemeService } from '@app/shared/services/theme/theme.service';
import { TranslationService } from '@app/shared/services/i18n';

@Component({
  selector: 'xea-verify',
  templateUrl: './verify.component.html',
  styleUrls: ['./verify.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VerifyComponent implements OnInit, OnDestroy {
  @lazyInject(Location) public location: Location;

  public token: string = '';
  public curNavigation: Navigation;

  public subscriptions: Subscription[] = [];

  public constructor(
    private router: Router,
    private xprtDataService: XprtDataService,
    private tokenSessionService: TokenSessionService,
    private dmDataService: DmDataService,
    private themeService: ThemeService,
    private translationService: TranslationService,
  ) {
    this.tokenSessionService.removeToken(SITE_ID);
    this.tokenSessionService.removeToken(COMPANY_ID);
    this.tokenSessionService.removeToken(SITE_TOKEN);
    this.tokenSessionService.removeToken(EMPLOYEE_ID);
    this.tokenSessionService.removeToken(EVENT_ID);
    this.subscriptions.push(
      this.tokenSessionService.token$.subscribe((token: string) => {
        if (!!token) {
          this.checkToken(token);
        }
      }),
    );
    this.curNavigation = this.router.getCurrentNavigation();
  }

  public ngOnInit(): void {}

  public checkToken(token: string) {
    this.xprtDataService
      .checkToken(token)
      .pipe(
        rx.map(() => this.parseSiteToken(token)),
        rx.switchMap((siteToken: IToken) => {
          const companyId = get(siteToken, 'company_id');

          if (!isEmpty(companyId)) {
            this.tokenSessionService.saveToken(companyId, COMPANY_ID);
          } else {
            return observableThrowError(VerifyErrorType.CompanyId);
          }

          const siteId = get(siteToken, 'site_id');
          if (!isEmpty(siteId)) {
            this.tokenSessionService.saveToken(siteId, SITE_ID);
          } else {
            return observableThrowError(VerifyErrorType.SiteId);
          }
          this.tokenSessionService.saveToken(token, SITE_TOKEN);

          return this.xprtDataService
            .getCompanyById(companyId, {
              select: ['name', 'subscriptions'],
            })
            .pipe(
              rx.switchMap((companyData) => {
                if (
                  SubscriptionCheckService.IsCompanyLevelActive(companyData)
                ) {
                  return observableOf(siteToken);
                }

                return observableThrowError(VerifyErrorType.SubsXVI);
              }),
            );
        }),
        rx.switchMap(() =>
          combineLatest([
            this.dmDataService.getSitePreferences().pipe(
              rx.tap((res) => {
                this.dmDataService.sitePreferences = head(res) as any;
                this.tokenSessionService.saveToken(
                  get(head(res), [
                    'preferences',
                    'applications',
                    'xpos',
                    'logo_url',
                  ]),
                  LOGO_URL,
                );
              }),
            ),
            this.dmDataService.fetchCompanyPreferences().pipe(
              rx.tap((res) => {
                this.dmDataService.companyPreferences = res;
              }),
              rx.catchError(() => observableOf({})),
            ),
          ]),
        ),
        rx.switchMap(() => this.themeService.initTheme()),
        rx.switchMap(async () => this.translationService.initTranslation()),
        catchError((error) => {
          this.routerToError(
            Object.values(VerifyErrorType).includes(error)
              ? error
              : VerifyErrorType.Token,
          );
          return observableOf();
        }),
      )
      .subscribe(() => {
        this.router.navigateByUrl('sign-in', {
          state: this.curNavigation?.extras?.state,
        });
      });
  }

  public parseSiteToken(token: string): IToken {
    return JSON.parse(atob(token.split('.')[1]));
  }

  public ngOnDestroy(): void {
    forEach(this.subscriptions, (subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }

  public routerToError(type: VerifyErrorType) {
    const errorMessage = {
      token: I18n.VerifyStrings.InvalidUrl,
      site_id: I18n.VerifyStrings.SiteNotFound,
      company_id: I18n.VerifyStrings.CompanyNotFound,
      subs_xvi: I18n.VerifyStrings.NotSubsXvi,
    };

    this.router.navigateByUrl('error', {
      state: {
        message: errorMessage[type],
      },
    });
  }
}
